import ArticleInjector from "components/ads/ArticleInjector";
import ArticleRelatedContentModule from "components/article/ArticleRelatedContentModule";
import Native from "components/ads/Native";
import HousePromo from "components/ads/HousePromo";
import {
  InjectedTypes,
  ArticleElementTypes,
  RuleInPixels,
  ElementPosition,
  OverlapBlock,
  Rule,
} from "types/injector";
import { ArticleRelatedContentType } from "types/api";
import {
  getInjectionRules,
  convertRulesToPx,
} from "utils/csi/getInjectionRules";
import getInjectionOrder from "utils/csi/getInjectionOrder";
import { MutableRefObject } from "react";
import log from "utils/csi/injectorLogger";
import doesMatchType from "utils/csi/doesMatchType";
import { detectOverlaps, inOverlapBlock } from "utils/csi/detectOverlaps";
import getPositions from "utils/csi/getPositions";

// approximate height
export const RECIRC_HEIGHT = 476;

// The number of times the injector has run
let RUN_COUNT = 0;

/**
 * Checks if the after part of the rule is ok
 * @returns boolean if the after: part of the rule passes
 */
const afterRuleOk = function (
  rule: RuleInPixels,
  i: number,
  elementsArr: HTMLElement[],
  componentsArr: JSX.Element[],
  positions: ElementPosition[],
  overlapBlocks: OverlapBlock[],
  alreadyInjected: { type: InjectedTypes; afterIndex: number }[]
) {
  if (!rule.after) {
    return true;
  }

  const logforRule = `AFTER: Checking rule ${rule.item} should be ${rule.after} px after ${rule.from}. `;

  // count back from here to find the last item of the rule.from type
  let distanceToJ = 0;
  let reachedBeginning = false;

  // travel upwards through the elements
  // j is the element we are comparing against
  // When measuring up, count the element you start after first
  for (let j = i; j >= -1; j--) {
    // the distance we've traveled is the very bottom of the element above where we started
    // to the very top of the element we've just checked
    // [position j] ___ bottom margin of this
    // ...
    // [position i] __ bottom margin of this
    // <maybe inject item here>
    distanceToJ =
      positions[i].bottomMargin -
      (j === -1 ? positions[0].topMargin : positions[j].bottomMargin);

    // we have reached the beginning of the article
    if (j == -1) {
      log.debug(`${logforRule} Reached the beginning of the article`);
      reachedBeginning = true;
      break;
    }
    // we have traveled at least the distance we're looking for and we're not in an overlap
    // block
    else if (distanceToJ >= rule.after && !inOverlapBlock(j, overlapBlocks)) {
      log.debug(`${logforRule} Traveled ${distanceToJ}px, exiting search`);
      break;
    }
    // check if it matches the from rule
    else if (
      doesMatchType(
        rule.from,
        elementsArr[j],
        componentsArr[j],
        j,
        alreadyInjected
      )
    ) {
      break;
    }
  }

  if (distanceToJ < rule.after) {
    if (reachedBeginning) {
      // we're looking for the beginning and it's too close
      if (rule.from === ArticleElementTypes.Begin) {
        log.debug(
          `${logforRule} FAIL: We're looking for the beginning and it's too close at ${distanceToJ}px`
        );
        return false;
      }
      // we never ran into an element of that type, so we're good
      else {
        log.debug(
          `${logforRule} PASS: Never ran into an element of ${rule.from} type`
        );
        return true;
      }
    }
    // didn't reach beginning, hit a relevant element and it's too close
    log.debug(
      `${logforRule} FAIL: Hit an element of type ${rule.from}, and it's too close at ${distanceToJ}px`
    );
    return false;
  }
  return true;
};

/**
 * Checks if the before part of the rule is ok
 * @returns boolean if the before: part of the rule passes
 */
const beforeRuleOk = function (
  rule: RuleInPixels,
  i: number,
  elementsArr: HTMLElement[],
  componentsArr: JSX.Element[],
  positions: ElementPosition[],
  overlapBlocks: OverlapBlock[]
) {
  if (!rule.before) {
    return true;
  }

  // count forward from here to find the next item of the rule.from type
  let reachedEnd = false;
  let distanceToJ = 0;

  const logforRule = `BEFORE: Checking rule ${rule.item} should be ${rule.before} px before ${rule.from}. `;

  // travel downwards through the elements
  // i is index of the element we would like to insert after
  // j is the position of the thing we are checking against the rule as we walk through the array
  for (let j = i + 1; j <= elementsArr.length; j++) {
    const isEndOfArticle = j === elementsArr.length;

    // the distance we've traveled is the very top of the element after where we started
    // to the very bottom of the element we've just checked

    // This is weird!
    //
    //  [Position i]
    //  <maybe put injected thing here>
    //  [position i+1... position j]
    //
    // We want to measure from the y-index the ad will display at, which is the top
    // of the _following_ element's top margin. It is NOT necessarily the same as the bottom
    // of position i's bottom margin because margins collapse.
    distanceToJ =
      (isEndOfArticle
        ? positions[elementsArr.length - 1].bottomMargin
        : positions[j].topMargin) - positions[i + 1].topMargin;

    // we've reached the end of the article
    if (isEndOfArticle) {
      log.debug("Reached the end of the article");
      reachedEnd = true;
      break;
    }
    // we have traveled at least the distance we're looking for and we're not inside of an
    // overlap block
    else if (distanceToJ >= rule.before && !inOverlapBlock(j, overlapBlocks)) {
      log.debug(`${logforRule} Traveled ${distanceToJ}px, exiting search`);
      break;
    }
    // check if it matches the from rule
    else if (doesMatchType(rule.from, elementsArr[j], componentsArr[j], j)) {
      break;
    }
  }

  if (distanceToJ < rule.before) {
    if (reachedEnd) {
      // we're looking for the end and it's too close
      if (rule.from === ArticleElementTypes.End) {
        log.debug(
          `${logforRule} FAIL: The end of the article is too close at ${distanceToJ}px`
        );
        return false;
      }
      // we never ran into an element of the rule.from type, so we're good
      else {
        log.debug(
          `${logforRule} PASS: Couldn't find item of ${rule.from} type, so rule passes`
        );
        return true;
      }
    }

    // didn't reach end, we hit a relevant element and it's too close
    log.debug(
      `${logforRule} FAIL: Item of type ${rule.from} is too close at ${distanceToJ}px`
    );
    return false;
  }
  return true;
};

/**
 * Return a React Component based on the InjectedType
 * @param type InjectedTypes
 * @return React.Component
 */
export const getInjectedComponent = (
  type: InjectedTypes,
  num: number
): JSX.Element => {
  const key = `inject-${RUN_COUNT}`;
  switch (type) {
    case InjectedTypes.Ad:
      return (
        <ArticleInjector key={`${key}-${num}`} targetingPos={`csi-ad-${num}`} />
      );
    case InjectedTypes.Native:
      return <Native key={`${key}-${num}`} />;
    case InjectedTypes.House:
      // Prepopulate a prop
      return <HousePromo isSwan={true} key={`${key}-${num}`} />;
    case InjectedTypes.Recirc:
      return (
        <ArticleRelatedContentModule
          key={`${key}-${num}`}
          __typename="ArticleRelatedContentModule"
          contentType={ArticleRelatedContentType.Curated}
          count={1}
        />
      );
    default:
      const EmptyElement = () => <div></div>;
      return <EmptyElement key={`${key}-${num}`} />;
  }
};

// Only check rules that are relevant to the kind of item I currently want to inject
function findRelevantRules(
  rules: RuleInPixels[],
  toInject: InjectedTypes,
  index: number
) {
  const relevantRules = rules.filter((rule) => {
    if (rule.item !== InjectedTypes.Any && rule.item !== toInject) {
      return false;
    }
    if (typeof rule.index !== "undefined" && rule.index !== index) {
      return false;
    }
    if (
      typeof rule.indexNotEqual !== "undefined" &&
      rule.indexNotEqual == index
    ) {
      return false;
    }
    return true;
  });
  log.debug(
    `Next item (#${index}) to inject is a ${toInject}. Found ${relevantRules.length} relevant rules`
  );
  return relevantRules;
}

/**
 * Check that every component has an associated ref
 */
function sanityCheck(refs: HTMLElement[], componentsArr: JSX.Element[]) {
  for (let i = 0; i < componentsArr.length; i++) {
    if (!componentsArr[i]) {
      throw new Error(`Component at position ${i} is missing `);
    }
    if (!refs[i]) {
      throw new Error(
        `${componentsArr[i].props.__typename} at position ${i} has no associated ref `
      );
    }
  }
}

/**
 * Do the injection
 * @param componentsArr An array of components represented in the article
 * @param refs The artcle element array
 * @param isMobile if the browser is mobile
 * @param isRecircFullwidth if the recirc module is displayed at full width
 * @param hasRecirc if the article already has a recirc module
 * @param hasBento if the article has a Bento module
 * @param injectionRules an optional override of injection rules. For testing
 * @param injectionOrder an optional override of items to inject. For testing
 */
export const injectItems = function (
  componentsArr: JSX.Element[],
  refs: MutableRefObject<HTMLElement[]>,
  activeTestGroup: string | null,
  isMobile: boolean,
  isRecircFullwidth: boolean,
  hasRecirc: boolean,
  hasBento: boolean,
  injectionRules?: Rule[],
  injectionOrder?: InjectedTypes[]
) {
  // Increment the number of times this has run
  RUN_COUNT++;
  const rules: RuleInPixels[] = convertRulesToPx(
    injectionRules || getInjectionRules(isMobile, activeTestGroup)
  );
  const injectionList =
    injectionOrder ||
    getInjectionOrder(hasRecirc, hasBento, isMobile, activeTestGroup);

  try {
    sanityCheck(refs.current, componentsArr);
  } catch (e) {
    console.error(e);
    return componentsArr;
  }

  log.debug(`----- START CLIENT SIDE INJECTION RUN ${RUN_COUNT} -----`);
  log.debug({ hasRecirc, hasBento, isMobile, rules, injectionList });

  // cache the positions and detect overlapping elements
  const positions: ElementPosition[] = getPositions(
    refs.current,
    componentsArr,
    isMobile
  );
  const overlapBlocks = detectOverlaps(positions);
  log.debug({ positions, overlapBlocks });

  // we need to change the type here so we can add objects that we are injecting, and make a
  // copy of the refs array so we don't modify the original
  const elementsArr = [...refs.current];

  // splices in injected items
  let currentToInject = 0;
  let toInject = injectionList[currentToInject];
  let relevantRules = findRelevantRules(rules, toInject, currentToInject);
  const newComponentsArr = [...componentsArr];
  // there has been an item injected after these indicies
  const alreadyInjected: { type: InjectedTypes; afterIndex: number }[] = [];

  for (let i = 0; i < componentsArr.length - 1; i++) {
    // nothing left to inject
    if (!toInject) {
      break;
    }

    // if this index is inside of an overlap block (and not the end of one), we can skip
    const overlapBlock = inOverlapBlock(i, overlapBlocks);
    if (overlapBlock && i != overlapBlock.end) {
      log.debug(`MAIN LOOP: Position ${i} is in an overlap block, skipping`);
      continue;
    }

    // if we're 0px further than last time, we can skip.
    if (positions[i].topMargin == positions[i].bottomMargin) {
      log.debug(`MAIN LOOP: Position ${i} has 0 height, skipping`);
      continue;
    }

    log.groupCollapsed(
      `MAIN LOOP: Can we inject a ${toInject} after position ${i}?`
    );

    // loop through relevant rules
    let allRulesPass = true;
    for (const rule of relevantRules) {
      const afterRulePasses = afterRuleOk(
        rule,
        i,
        elementsArr,
        componentsArr,
        positions,
        overlapBlocks,
        alreadyInjected
      );
      if (!afterRulePasses) {
        log.groupEnd();
        log.debug(`🔻 Rule.after fails`, rule);
        allRulesPass = false;
        break;
      }

      // This element floats, so in order to check that it will be far enough away from elements
      // below and next to it, we have to add its height.
      const beforeRule = { ...rule };
      if (
        beforeRule.before &&
        toInject == InjectedTypes.Recirc &&
        !isRecircFullwidth
      ) {
        beforeRule.before += RECIRC_HEIGHT;
      }

      // check before rule
      const beforeRulePasses = beforeRuleOk(
        beforeRule,
        i,
        elementsArr,
        componentsArr,
        positions,
        overlapBlocks
      );
      if (!beforeRulePasses) {
        log.groupEnd();
        log.debug(`🔺 Rule.before fails`, rule);
        allRulesPass = false;
        break;
      }
    }

    if (allRulesPass) {
      log.groupEnd();
      log.debug(`✅ ALL RULES PASS, injecting ${toInject} after position ${i}`);
      const InjectedComponent = getInjectedComponent(toInject, currentToInject);
      newComponentsArr.splice(
        // i is the element in the initial array we want to inject after.
        // 1. Compensate for elements already injected
        // 2. +1 to inject *after* the element we are checking
        i + alreadyInjected.length + 1,
        0,
        InjectedComponent
      );
      alreadyInjected.push({ afterIndex: i, type: toInject });

      // go to the next item to inject
      currentToInject++;
      toInject = injectionList[currentToInject];
      relevantRules = toInject
        ? findRelevantRules(rules, toInject, currentToInject)
        : [];
    }
  }

  return newComponentsArr;
};
